import React from "react";
import { Button, Form, Label, Message, Modal, Select } from "semantic-ui-react";
import CustomerReviewAction from "@common/redux/actions/CustomerReviewAction";
import useCustomerReview from "@common/components/hooks/useCustomerReview";
import { useEffect } from "react";
import useLogin from "@common/components/hooks/useLogin";
import { useSelector } from "react-redux";

const CustomerReviewFormModal = () => {
  const {
    selectedRecord,
    setReviewModalState,
    selectedReview,
    questions: apiQuestions,
    open,
    form,
    errors,
    onCompleteClick,
    onReviewChange,
    inEditMode,
    onFormLoad,
  } = useCustomerReview();

  const { optionComplianceAllowEdit } = useSelector((state) => state.logIn) ?? {
    optionComplianceAllowEdit: true,
  };

  const [{ isDesktop }] = useLogin();

  const questions = React.useMemo(() => {
    if (!apiQuestions || apiQuestions.length === 0) {
      return [];
    }

    const guidToFieldName = {};
    apiQuestions.forEach((q) => {
      guidToFieldName[q.guid] = q.name;
    });
    return apiQuestions.map((q) => {
      if (q.parentGuid && q.condition !== null && q.condition !== undefined) {
        const parentFieldName = guidToFieldName[q.parentGuid];
        if (parentFieldName) {
          const shouldShow = form[parentFieldName] === q.condition;
          return { ...q, hidden: !shouldShow };
        }
      }
      return q;
    });
  }, [apiQuestions, form]);

  const disableEditButton =
    (selectedRecord?.compliance?.review?.isClosed ?? false) ||
    optionComplianceAllowEdit === false;

  const disableEdit = disableEditButton || !inEditMode;

  useEffect(() => {
    if (open) onFormLoad();
  }, [open, selectedReview]);

  const options = selectedRecord?.compliance?.reviews ?? [];

  const mappedOptions = (Array.isArray(options) ? options : []).map((option) => ({
    label: (
      <Label
        color={option.reviewStatus?.colour?.toLowerCase()}
        content={option.reviewStatus?.name}
      />
    ),
    text: option.reviewDate,
    value: option?.id,
  }));

  return (
    <Modal open={open} size="large">
      <Modal.Header>Customer Interaction Review</Modal.Header>
      <Modal.Content>
        {errors && typeof errors === 'object' && Object.keys(errors).length > 0 && (
          <Message error>
            <Message.Header>
              Please correct the following errors on your form
            </Message.Header>
            <ul>
              {Object.values(errors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Message>
        )}

        <Form>
          {isDesktop && options.length > 1 && (
            <Form.Field>
              <label>Select Review:</label>
              <Select
                className="w-40"
                options={mappedOptions}
                onChange={onReviewChange}
                value={selectedReview}
              />
            </Form.Field>
          )}
          {!selectedReview && (questions || []).length === 0 && (
            <Message info>Loading questions...</Message>
          )}
          {(questions || []).map((props) => {
            const { Component, name } = props;
            return (
              <Component
                key={name}
                {...props}
                error={errors && errors[name]}
                disabled={disableEdit}
              />
            );
          })}
        </Form>
      </Modal.Content>
      <Modal.Actions style={{ textAlign: "left" }}>
        <Button
          negative
          content="Back"
          icon="left arrow"
          onClick={() => {
            inEditMode && CustomerReviewAction.reset();
            CustomerReviewAction.dispatch({
              open: false,
              inEditMode: false,
            });
          }}
        />

        <Button
          icon={inEditMode ? "close" : "edit"}
          color="blue"
          content={inEditMode ? "Cancel" : "Edit"}
          onClick={() => {
            inEditMode && CustomerReviewAction.reset();
            setReviewModalState({ inEditMode: !inEditMode });
          }}
          disabled={disableEditButton}
        />

        <Button
          positive
          content="Complete Review"
          disabled={disableEdit}
          icon="check"
          floated="right"
          onClick={onCompleteClick}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default CustomerReviewFormModal;
